import {
  IBankAccount,
  useBankAccount,
  useBankAccountFieldsConfig,
} from '@commenda/api';
import { useFormTemplateKey } from '@commenda/api/form-builder/form-template-keys';
import { useFormsTemplate } from '@commenda/api/form-builder/forms-templates';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DocViewerComponent,
  Icons,
} from '@commenda/kakashi';
import { LoadingIndicator } from '@commenda/kakashi/src/components/LoadingIndicator';
import { downloadFileFromLink } from '@commenda/utils';
import { getSignedUrlForHostedFileUrl } from 'node_modules/@commenda/kakashi';
import { useContext, useMemo, useState } from 'react';
import { formatAccountType } from '../../../bankAccount/utils';
import { GridView, TGridViewDataPoint } from '../../../components/GridView';
import { DataPointButton } from '../../../Corporation/common/DataPoint/DataPointButton';
import { useNotification } from '../../../hooks/useNotification';
import { getFieldDef } from '../../config/country-bank-utils';
import { FormContext } from '../../hooks/FormContext';
import { PREVIEW_FILE_URL_SCHEME } from '../../preview/test-value-registry';
import { formatNumberForDisplay } from '../../utils/number-display';
import { readPartnershipStructure } from './Ownership/copy';
import { formatOwnershipValue, isOwnershipValue } from './Ownership/format';

const formatFieldName = (fieldName: string): string => {
  return fieldName
    .replace(/([A-Z])/g, ' $1')
    .replace(/[_-]/g, ' ')
    .replace(/^\s/, '')
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
    .trim();
};

// Fallback label formatter
const formatFieldId = (fieldId: string): string => {
  return fieldId
    .replace(/([A-Z])/g, ' $1')
    .replace(/[_-]/g, ' ')
    .replace(/^\s/, '')
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
    .trim();
};

// Helper to detect if an object is an AddressInput
const isAddressInput = (value: unknown): boolean => {
  if (
    typeof value !== 'object' ||
    value === null ||
    Array.isArray(value) ||
    value instanceof File
  ) {
    return false;
  }
  const obj = value as Record<string, unknown>;
  const addressKeys = [
    'addressLine1',
    'addressLine2',
    'postalCode',
    'city',
    'state',
    'country',
  ];
  return addressKeys.some((key) => key in obj);
};

// Helper to detect if an object is a FileUpload
const isFileUpload = (value: unknown): boolean => {
  if (
    typeof value !== 'object' ||
    value === null ||
    Array.isArray(value) ||
    value instanceof File
  ) {
    return false;
  }
  const obj = value as Record<string, unknown>;
  const fileKeys = ['fileName', 'fileUrl', 'documentType'];
  return fileKeys.some((key) => key in obj);
};

// A synthetic file value injected by preview pre-fill (no real upload behind
// it). Its fileUrl uses the preview:// scheme, so download/preview must not
// attempt a signed-URL fetch.
const isSyntheticPreviewFile = (value: unknown): boolean => {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return false;
  }
  const { fileUrl } = value as Record<string, unknown>;
  return (
    typeof fileUrl === 'string' && fileUrl.startsWith(PREVIEW_FILE_URL_SCHEME)
  );
};

const isMultiFileUpload = (value: unknown): boolean => {
  if (!Array.isArray(value) || value.length === 0) {
    return false;
  }
  return value.every((item) => {
    if (typeof item !== 'object' || item === null || item instanceof File) {
      return false;
    }
    const obj = item as Record<string, unknown>;
    const fileKeys = ['fileName', 'fileUrl', 'documentType'];
    return fileKeys.some((key) => key in obj);
  });
};

// Detect array of nested form entries (objects that may have __meta and field keys)
function isNestedFormEntries(
  value: unknown
): value is Record<string, unknown>[] {
  if (!Array.isArray(value) || value.length === 0) return false;
  return value.every(
    (item) =>
      typeof item === 'object' &&
      item !== null &&
      !Array.isArray(item) &&
      !(item instanceof File)
  );
}

// KeyPersons data: { selectedIds: string[], entries: Record<personId, formData> }
function isKeyPersonsData(value: unknown): value is {
  selectedIds: string[];
  entries: Record<string, Record<string, unknown>>;
} {
  if (typeof value !== 'object' || value === null || Array.isArray(value))
    return false;
  const obj = value as Record<string, unknown>;
  return (
    Array.isArray(obj.selectedIds) &&
    typeof obj.entries === 'object' &&
    obj.entries !== null
  );
}

const CUSTOM_COMPONENT_TYPES = new Set(['NestedForm', 'KeyPersons']);

// Value formatter
// componentMap: Record<string, FormComponent> - maps fieldId to component definition
const formatValue = (
  value: unknown,
  fieldId?: string,
  componentMap?: Record<string, unknown>
): string => {
  if (value === null || value === undefined || value === '') {
    return '—';
  }

  // Try to look up option label for RadioGroup, Select, and CheckboxGroup components
  // For repeatable fieldIds (saveKey.index.fieldId), use base fieldId for componentMap lookup
  const baseFieldId = fieldId?.includes('.')
    ? fieldId.split('.').pop()
    : fieldId;

  if (baseFieldId && componentMap) {
    const component = componentMap[baseFieldId] as
      | {
          component?: string;
          options?: Array<{ value: string; label: string }>;
          props?: Record<string, unknown>;
        }
      | undefined;

    if (
      component?.component === 'NumberInput' ||
      (component?.component === 'AutoDetectField' && typeof value === 'number')
    ) {
      const formatted = formatNumberForDisplay(value, component.props);
      if (formatted !== null) return formatted;
    }

    if (
      component &&
      component.options &&
      Array.isArray(component.options) &&
      component.options.length > 0
    ) {
      // Handle CheckboxGroup: value is an object like { "option1": true, "option2": true }
      if (component.component === 'CheckboxGroup') {
        if (
          typeof value === 'object' &&
          value !== null &&
          !Array.isArray(value)
        ) {
          const obj = value as Record<string, boolean>;
          const selectedLabels: string[] = [];

          Object.entries(obj).forEach(([optionValue, isSelected]) => {
            if (isSelected === true) {
              // Normalize values for comparison - handle null/undefined/empty strings
              const normalizedOptionValue =
                optionValue != null ? String(optionValue).trim() : '';

              if (normalizedOptionValue !== '') {
                const option = component.options.find((opt) => {
                  // Handle null/undefined option values
                  if (opt.value == null) {
                    return normalizedOptionValue === '';
                  }
                  // Normalize and compare option values
                  const normalizedOptValue = String(opt.value).trim();
                  return normalizedOptValue === normalizedOptionValue;
                });

                if (option) {
                  // Use label if available, otherwise fallback to the option value
                  const displayLabel =
                    option.label?.trim() || String(option.value);
                  if (displayLabel) {
                    selectedLabels.push(displayLabel);
                  }
                } else {
                  // Fallback: if option not found, show the raw value
                  selectedLabels.push(normalizedOptionValue);
                }
              }
            }
          });

          return selectedLabels.length > 0 ? selectedLabels.join(', ') : '—';
        }
      }

      // Handle RadioGroup and Select: value is a single string
      if (
        component.component === 'RadioGroup' ||
        component.component === 'Select'
      ) {
        const option = component.options.find(
          (opt) => String(opt.value) === String(value)
        );
        if (option && option.label) {
          return option.label;
        }
      }
    }
  }

  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }
  if (typeof value === 'object') {
    if (value instanceof File) {
      return value.name;
    }
    if (Array.isArray(value)) {
      // Handle MultiFileUpload: array of FileBasic objects
      if (isMultiFileUpload(value)) {
        const fileNames = value
          .map((file) => {
            const fileName = (file as Record<string, unknown>).fileName;
            return fileName != null && fileName !== ''
              ? String(fileName)
              : null;
          })
          .filter((name): name is string => name !== null);
        return fileNames.length > 0 ? fileNames.join(',\n') : '—';
      }
      // Handle other arrays
      return value.length > 0
        ? value.map((v) => formatValue(v, undefined, componentMap)).join(', ')
        : '—';
    }
    const obj = value as Record<string, unknown>;

    // Ownership node: summarise as an owner list, not a raw object dump.
    if (isOwnershipValue(obj)) {
      const node = baseFieldId ? componentMap?.[baseFieldId] : undefined;
      return formatOwnershipValue(
        value,
        readPartnershipStructure(
          (node as { props?: Record<string, unknown> } | undefined)?.props
        )
      );
    }

    // Handle FileUpload: return only fileName
    if (isFileUpload(obj)) {
      const fileName = obj.fileName;
      if (fileName === null || fileName === undefined || fileName === '') {
        return '—';
      }
      return String(fileName);
    }

    // Handle AddressInput: return comma-separated values (no labels)
    if (isAddressInput(obj)) {
      const addressParts: string[] = [];
      const fields = [
        'addressLine1',
        'addressLine2',
        'postalCode',
        'city',
        'state',
      ];
      for (const field of fields) {
        const fieldValue = obj[field];
        if (
          fieldValue !== null &&
          fieldValue !== undefined &&
          fieldValue !== ''
        ) {
          addressParts.push(String(fieldValue));
        }
      }
      return addressParts.length > 0 ? addressParts.join(', ') : '—';
    }

    // Nested form entry: object with __meta – display using labels, skip __meta
    const meta = obj.__meta as
      | { labels?: Record<string, string>; formName?: string }
      | undefined;
    if (meta !== undefined && typeof meta === 'object') {
      const dataEntries = Object.entries(obj).filter(
        ([key]) => key !== '__meta'
      );
      if (dataEntries.length > 0) {
        const labelMap = meta.labels ?? {};
        const parts = dataEntries
          .filter(([, v]) => v !== null && v !== undefined && v !== '')
          .map(([k, v]) => {
            const label = labelMap[k] || formatFieldId(k);
            return `${label}: ${formatValue(v, k, componentMap)}`;
          });
        return parts.length > 0 ? parts.join(', ') : '—';
      }
    }

    // Default object formatting for other types
    const parts = Object.entries(obj)
      .filter(([, v]) => v !== null && v !== undefined && v !== '')
      .map(
        ([k, v]) =>
          `${formatFieldName(k)}: ${formatValue(v, undefined, componentMap)}`
      );
    return parts.length > 0 ? parts.join(', ') : '—';
  }
  return String(value);
};

type SummaryModule = {
  id: string;
  name?: string;
  description?: string;
  saveKey?: string;
  repeatable?: boolean;
  fieldIds?: string[];
};

type SummaryStep = {
  id: string;
  title?: string;
  modules: SummaryModule[];
};

// =============================================================================
// NestedFormSummarySection
// Fetches the linked template's label map and renders the entries with proper
// field labels. Works for both new entries (with __meta.labels) and old ones.
// =============================================================================

interface NestedFormSummarySectionProps {
  fieldId: string;
  entries: Record<string, unknown>[];
  keyId: string;
  sectionLabel: string;
  onDownload?: (fileValue: unknown) => void;
  onPreview?: (fileValue: unknown) => void;
}

type TemplateModuleGroup = {
  moduleId: string;
  moduleName: string;
  fieldIds: string[];
  labelMap: Record<string, string>;
  saveKey?: string;
  repeatable?: boolean;
};

function NestedFormSummarySection({
  fieldId,
  entries,
  keyId,
  sectionLabel,
  onDownload,
  onPreview,
}: NestedFormSummarySectionProps) {
  const { key } = useFormTemplateKey(keyId || null);
  const { template } = useFormsTemplate(key?.liveFormId ?? null);

  // Build module-grouped structure from the linked template's DSL
  const templateModules = useMemo((): TemplateModuleGroup[] => {
    if (!template?.dsl) return [];
    const dsl = template.dsl;
    const groups: TemplateModuleGroup[] = [];

    (dsl.steps ?? []).forEach((step) => {
      (step.modules ?? []).forEach((mod) => {
        const fieldIds: string[] = [];
        const labelMap: Record<string, string> = {};

        (mod.components ?? []).forEach((comp) => {
          if (comp.fieldId) {
            fieldIds.push(comp.fieldId);
            if (comp.label) {
              labelMap[comp.fieldId] = comp.label;
            }
          }
        });

        if (fieldIds.length > 0) {
          groups.push({
            moduleId: mod.id,
            moduleName: mod.name || mod.id,
            fieldIds,
            labelMap,
            saveKey: mod.saveKey,
            repeatable: Boolean(mod.repeatable?.enabled),
          });
        }
      });
    });

    return groups;
  }, [template]);

  // Map fieldId -> keyId for inner NestedForm components (for recursive rendering)
  const fieldIdToNestedKeyId = useMemo((): Record<string, string> => {
    const map: Record<string, string> = {};
    if (!template?.dsl) return map;
    const dsl = template.dsl;
    (dsl.steps ?? []).forEach((step) => {
      (step.modules ?? []).forEach((mod) => {
        (mod.components ?? []).forEach((comp) => {
          if (
            comp.fieldId &&
            comp.component === 'NestedForm' &&
            (comp.props as { keyId?: string } | undefined)?.keyId
          ) {
            map[comp.fieldId] = (comp.props as { keyId: string }).keyId;
          }
        });
      });
    });
    return map;
  }, [template]);

  // Flat label map for fallback (from __meta or template)
  const flatLabelMap = useMemo((): Record<string, string> => {
    const map: Record<string, string> = {};
    for (const mod of templateModules) {
      Object.assign(map, mod.labelMap);
    }
    return map;
  }, [templateModules]);

  const getMetaLabelMap = (
    entry: Record<string, unknown>
  ): Record<string, string> => {
    const meta = entry.__meta as
      | { labels?: Record<string, string> }
      | undefined;
    return meta?.labels ?? {};
  };

  const formName =
    template?.name ||
    (entries[0]?.__meta as { formName?: string } | undefined)?.formName ||
    sectionLabel;

  const hasModules = templateModules.length > 0;

  return (
    <div className="space-y-3">
      {/* <h4 className="text-text-primary txt-medium-semibold">{formName}</h4> */}
      {entries.map((entryObj, entryIndex) => {
        const metaLabelMap = getMetaLabelMap(entryObj);
        const entryHeading =
          entries.length > 1 ? `${formName} ${entryIndex + 1}` : null;

        if (hasModules) {
          return (
            <div key={`${fieldId}-${entryIndex}`} className="space-y-3">
              {/* {entryHeading && (
                <h5 className="text-text-secondary txt-small-medium">
                  {entryHeading}
                </h5>
              )} */}
              {templateModules.map((mod) => {
                // Repeatable modules store instances as an array under their
                // saveKey (module id when absent), not per fieldId.
                if (mod.repeatable) {
                  const instanceKey = mod.saveKey || mod.moduleId;
                  const raw = entryObj[instanceKey];
                  const instances = Array.isArray(raw) ? raw : [];

                  const blocks = instances
                    .map((instance: unknown, instanceIndex: number) => {
                      const obj = (instance ?? {}) as Record<string, unknown>;
                      const dataPoints = mod.fieldIds
                        .map((fId): TGridViewDataPoint | null => {
                          const val = obj[fId];
                          if (val === null || val === undefined || val === '')
                            return null;
                          return {
                            heading:
                              mod.labelMap[fId] ||
                              metaLabelMap[fId] ||
                              formatFieldId(fId),
                            value: formatValue(
                              val,
                              `${instanceKey}.${instanceIndex}.${fId}`
                            ),
                          };
                        })
                        .filter((dp): dp is TGridViewDataPoint => dp !== null);
                      return dataPoints.length > 0
                        ? { instanceIndex, dataPoints }
                        : null;
                    })
                    .filter(
                      (
                        b
                      ): b is {
                        instanceIndex: number;
                        dataPoints: TGridViewDataPoint[];
                      } => b !== null
                    );

                  if (blocks.length === 0) return null;

                  return (
                    <div key={mod.moduleId} className="space-y-2">
                      <h5 className="text-text-secondary txt-small-medium">
                        {mod.moduleName}
                      </h5>
                      {blocks.map((block) => (
                        <div
                          key={`${mod.moduleId}-${block.instanceIndex}`}
                          className="space-y-1"
                        >
                          {blocks.length > 1 && (
                            <p className="text-text-tertiary txt-small-regular">
                              {`${mod.moduleName} ${block.instanceIndex + 1}`}
                            </p>
                          )}
                          <GridView
                            dataPoints={block.dataPoints}
                            disableRowClick
                          />
                        </div>
                      ))}
                    </div>
                  );
                }

                const modEntries = mod.fieldIds
                  .map((fId): [string, unknown] | null => {
                    const val = entryObj[fId];
                    if (val === null || val === undefined || val === '')
                      return null;
                    return [fId, val];
                  })
                  .filter((e): e is [string, unknown] => e !== null);

                if (modEntries.length === 0) return null;

                // Split into regular fields and nested form fields
                const regularEntries: [string, unknown][] = [];
                const nestedFormFields: {
                  fId: string;
                  entries: Record<string, unknown>[];
                  nestedKeyId: string;
                  label: string;
                }[] = [];

                for (const [k, v] of modEntries) {
                  const nestedKeyId = fieldIdToNestedKeyId[k];
                  const entriesArray = Array.isArray(v)
                    ? v
                    : v != null &&
                        typeof v === 'object' &&
                        !Array.isArray(v) &&
                        !(v instanceof File)
                      ? [v as Record<string, unknown>]
                      : [];
                  if (
                    nestedKeyId &&
                    entriesArray.length > 0 &&
                    isNestedFormEntries(entriesArray)
                  ) {
                    nestedFormFields.push({
                      fId: k,
                      entries: entriesArray,
                      nestedKeyId,
                      label:
                        mod.labelMap[k] || metaLabelMap[k] || formatFieldId(k),
                    });
                  } else {
                    regularEntries.push([k, v]);
                  }
                }

                const dataPoints: TGridViewDataPoint[] = regularEntries.map(
                  ([k, v]) => {
                    const heading =
                      mod.labelMap[k] || metaLabelMap[k] || formatFieldId(k);
                    const dp: TGridViewDataPoint = {
                      heading,
                      value: formatValue(v, k),
                    };
                    if (
                      (isFileUpload(v) || isMultiFileUpload(v)) &&
                      (onDownload || onPreview)
                    ) {
                      dp.actions = [];
                      if (onPreview)
                        dp.actions.push({
                          icon: Icons.Eye,
                          tooltip: 'Preview',
                          onClick: () => onPreview(v),
                        });
                      if (onDownload)
                        dp.actions.push({
                          icon: Icons.Download01,
                          tooltip: 'Download',
                          onClick: () => onDownload(v),
                        });
                    }
                    return dp;
                  }
                );

                return (
                  <div key={mod.moduleId} className="space-y-2">
                    <h5 className="text-text-secondary txt-small-medium">
                      {mod.moduleName}
                    </h5>
                    {dataPoints.length > 0 && (
                      <GridView
                        dataPoints={dataPoints}
                        disableRowClick
                      />
                    )}
                    {nestedFormFields.map((nested) => (
                      <NestedFormSummarySection
                        key={`${fieldId}-${entryIndex}-${nested.fId}`}
                        fieldId={nested.fId}
                        entries={nested.entries}
                        keyId={nested.nestedKeyId}
                        sectionLabel={nested.label}
                        onDownload={onDownload}
                        onPreview={onPreview}
                      />
                    ))}
                  </div>
                );
              })}
            </div>
          );
        }

        // Fallback: flat rendering when no template structure is available
        const dataEntries = Object.entries(entryObj).filter(
          ([k, v]) =>
            k !== '__meta' && v !== null && v !== undefined && v !== ''
        );

        if (dataEntries.length === 0) return null;

        // Split into regular fields and nested form fields
        const regularEntries: [string, unknown][] = [];
        const nestedFormFields: {
          fId: string;
          entries: Record<string, unknown>[];
          nestedKeyId: string;
          label: string;
        }[] = [];

        for (const [k, v] of dataEntries) {
          const nestedKeyId = fieldIdToNestedKeyId[k];
          const entriesArray = Array.isArray(v)
            ? v
            : v != null &&
                typeof v === 'object' &&
                !Array.isArray(v) &&
                !(v instanceof File)
              ? [v as Record<string, unknown>]
              : [];
          if (
            nestedKeyId &&
            entriesArray.length > 0 &&
            isNestedFormEntries(entriesArray)
          ) {
            nestedFormFields.push({
              fId: k,
              entries: entriesArray,
              nestedKeyId,
              label: flatLabelMap[k] || metaLabelMap[k] || formatFieldId(k),
            });
          } else {
            regularEntries.push([k, v]);
          }
        }

        const dataPoints: TGridViewDataPoint[] = regularEntries.map(
          ([k, v]) => {
            const heading =
              flatLabelMap[k] || metaLabelMap[k] || formatFieldId(k);
            const dp: TGridViewDataPoint = {
              heading,
              value: formatValue(v, k),
            };
            if (
              (isFileUpload(v) || isMultiFileUpload(v)) &&
              (onDownload || onPreview)
            ) {
              dp.actions = [];
              if (onPreview)
                dp.actions.push({
                  icon: Icons.Eye,
                  tooltip: 'Preview',
                  onClick: () => onPreview(v),
                });
              if (onDownload)
                dp.actions.push({
                  icon: Icons.Download01,
                  tooltip: 'Download',
                  onClick: () => onDownload(v),
                });
            }
            return dp;
          }
        );

        return (
          <div key={`${fieldId}-${entryIndex}`} className="space-y-2">
            {entryHeading && (
              <h5 className="text-text-secondary txt-small-medium">
                {entryHeading}
              </h5>
            )}
            {dataPoints.length > 0 && (
              <GridView dataPoints={dataPoints} disableRowClick />
            )}
            {nestedFormFields.map((nested) => (
              <NestedFormSummarySection
                key={`${fieldId}-${entryIndex}-${nested.fId}`}
                fieldId={nested.fId}
                entries={nested.entries}
                keyId={nested.nestedKeyId}
                sectionLabel={nested.label}
                onDownload={onDownload}
                onPreview={onPreview}
              />
            ))}
          </div>
        );
      })}
    </div>
  );
}

interface FormSummaryProps {
  props?: {
    description?: string;
    steps?: SummaryStep[];
    modules?: SummaryModule[];
    fieldLabelMap?: Record<string, string>;
    componentMap?: Record<string, unknown>;
    corporationId?: number;
    showPreview?: boolean;
    showDownload?: boolean;
    showNestedFormData?: boolean;
  };
}

export const FormSummaryComponent = ({ props }: FormSummaryProps) => {
  const { formData } = useContext(FormContext);
  const { setNotification } = useNotification();
  const {
    description,
    modules = [],
    fieldLabelMap = {}, // optional: id/fieldId → "Step 1.Personal.Text Input"
    componentMap = {}, // optional: fieldId → FormComponent (for looking up options)
    corporationId,
    showPreview = true,
    showDownload = false,
    showNestedFormData = true,
  } = props || {};

  const { data: bankAccountsData } = useBankAccount({
    corporationId: corporationId ?? 0,
  });

  const { config: bankFieldsConfig } = useBankAccountFieldsConfig();

  const bankAccountsById = useMemo(() => {
    const map: Record<string, IBankAccount> = {};
    for (const acc of bankAccountsData?.accounts ?? []) {
      if (acc.id) map[acc.id] = acc;
    }
    return map;
  }, [bankAccountsData]);

  // Preview dialog state
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewFileUrl, setPreviewFileUrl] = useState<string | null>(null);
  const [previewFileName, setPreviewFileName] = useState<string | null>(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);

  const handleDownload = async (fileValue: unknown) => {
    if (!isFileUpload(fileValue) && !(fileValue instanceof File)) {
      return;
    }

    if (isSyntheticPreviewFile(fileValue)) {
      setNotification({
        show: true,
        title: 'Preview only',
        message: 'This is a placeholder test file — download is unavailable.',
        valid: false,
      });
      return;
    }

    try {
      if (fileValue instanceof File) {
        const url = URL.createObjectURL(fileValue);
        downloadFileFromLink(url, fileValue.name);
        URL.revokeObjectURL(url);
        setNotification({
          show: true,
          title: 'Success',
          message: 'File download started',
          valid: true,
        });
      } else {
        const fileObj = fileValue as Record<string, unknown>;
        const fileName = fileObj.fileName as string;
        const fileUrl = fileObj.fileUrl as string;
        const existingSignedUrl = fileObj.signedUrl as string | undefined;

        if (existingSignedUrl) {
          downloadFileFromLink(existingSignedUrl, fileName);
          setNotification({
            show: true,
            title: 'Success',
            message: 'File download started',
            valid: true,
          });
        } else if (fileUrl && typeof fileUrl === 'string') {
          const signedUrl = await getSignedUrlForHostedFileUrl(
            fileUrl,
            corporationId
          );
          downloadFileFromLink(signedUrl, fileName);
          setNotification({
            show: true,
            title: 'Success',
            message: 'File download started',
            valid: true,
          });
        } else {
          setNotification({
            show: true,
            title: 'Failed to download',
            message: 'File information is missing',
            valid: false,
          });
        }
      }
    } catch (error) {
      console.error('Error downloading file:', error);
      setNotification({
        show: true,
        title: 'Failed to download',
        message: 'An error occurred while downloading the file',
        valid: false,
      });
    }
  };

  const handlePreview = async (fileValue: unknown) => {
    if (!isFileUpload(fileValue) && !(fileValue instanceof File)) {
      return;
    }

    if (isSyntheticPreviewFile(fileValue)) {
      setNotification({
        show: true,
        title: 'Preview only',
        message: 'This is a placeholder test file — preview is unavailable.',
        valid: false,
      });
      return;
    }

    setIsLoadingPreview(true);
    setIsPreviewOpen(true);

    try {
      let signedUrl: string | null = null;
      let fileName = 'Preview file';

      if (fileValue instanceof File) {
        signedUrl = URL.createObjectURL(fileValue);
        fileName = fileValue.name;
      } else {
        const fileObj = fileValue as Record<string, unknown>;
        fileName = (fileObj.fileName as string) || 'Preview file';
        const existingSignedUrl = fileObj.signedUrl as string | undefined;
        const fileUrl = fileObj.fileUrl as string;

        if (existingSignedUrl) {
          signedUrl = existingSignedUrl;
        } else if (fileUrl && typeof fileUrl === 'string') {
          signedUrl = await getSignedUrlForHostedFileUrl(
            fileUrl,
            corporationId
          );
        }
      }

      setPreviewFileName(fileName);

      if (signedUrl) {
        setPreviewFileUrl(signedUrl);
      } else {
        setNotification({
          show: true,
          title: 'Failed to preview',
          message: 'File information is missing or could not be loaded',
          valid: false,
        });
        setIsPreviewOpen(false);
      }
    } catch (error) {
      console.error('Error previewing file:', error);
      setNotification({
        show: true,
        title: 'Failed to preview',
        message: 'An error occurred while loading the file preview',
        valid: false,
      });
      setIsPreviewOpen(false);
    } finally {
      setIsLoadingPreview(false);
    }
  };

  const handleClosePreview = () => {
    setIsPreviewOpen(false);
    setPreviewFileUrl(null);
    setPreviewFileName(null);
  };

  const isEmptyValue = (value: unknown) => {
    if (value === null || value === undefined || value === '') return true;
    if (
      typeof value === 'object' &&
      !Array.isArray(value) &&
      !(value instanceof File)
    ) {
      const obj = value as Record<string, unknown>;
      return !Object.values(obj).some(
        (v) => v !== null && v !== undefined && v !== ''
      );
    }
    return false;
  };

  const allFormEntries = Object.entries(formData).filter(
    ([, value]) => !isEmptyValue(value)
  );

  const getSectionEntries = (sectionName: string): Array<[string, unknown]> => {
    const sectionData = (formData as any)[sectionName];

    if (
      sectionData &&
      typeof sectionData === 'object' &&
      !Array.isArray(sectionData) &&
      !(sectionData instanceof File)
    ) {
      const obj = sectionData as Record<string, unknown>;
      return Object.entries(obj).filter(([, value]) => !isEmptyValue(value));
    }

    return allFormEntries.filter(([fieldId]) =>
      fieldId.startsWith(sectionName)
    );
  };

  const getModuleEntriesFlat = (
    module: SummaryModule
  ): Array<[string, unknown]> => {
    if (module.fieldIds && module.fieldIds.length > 0) {
      const fieldSet = new Set(module.fieldIds);
      return allFormEntries.filter(([fieldId]) => fieldSet.has(fieldId));
    }

    if (module.id) {
      return allFormEntries.filter(([fieldId]) =>
        fieldId.startsWith(module.id)
      );
    }

    return [];
  };

  /** For repeatable modules: returns instances with their entries */
  const getRepeatableModuleInstances = (
    module: SummaryModule
  ): Array<{
    instanceIndex: number;
    instanceLabel: string;
    entries: Array<[string, unknown]>;
  }> => {
    if (!module.repeatable || !module.fieldIds?.length) {
      return [];
    }

    const saveKey = module.saveKey || module.id;
    const raw = formData[saveKey];
    const instances = Array.isArray(raw) ? raw : [];

    return instances
      .map((instanceData: unknown, instanceIndex: number) => {
        const obj = (instanceData ?? {}) as Record<string, unknown>;
        const entries: Array<[string, unknown]> = module
          .fieldIds!.map((fieldId) => {
            const value = obj[fieldId];
            if (isEmptyValue(value)) return null;
            return [`${saveKey}.${instanceIndex}.${fieldId}`, value] as [
              string,
              unknown,
            ];
          })
          .filter((e): e is [string, unknown] => e !== null);

        if (entries.length === 0) return null;

        const instanceLabel =
          instances.length > 1
            ? `${module.name || formatFieldId(module.id)} ${instanceIndex + 1}`
            : module.name || formatFieldId(module.id);

        return { instanceIndex, instanceLabel, entries };
      })
      .filter((e): e is NonNullable<typeof e> => e !== null);
  };

  // Prefer component label, then label map, then formatted fieldId
  const getLabel = (fieldId: string) => {
    // For repeatable: "saveKey.index.fieldId" -> baseFieldId is the actual fieldId
    const baseFieldId =
      fieldId.includes('.') && /\.\d+\./.test(fieldId)
        ? fieldId.split('.').pop()
        : fieldId;

    const component = componentMap?.[baseFieldId || fieldId] as
      | { label?: string }
      | undefined;
    if (component?.label) {
      return component.label;
    }

    const mapped = fieldLabelMap[fieldId];
    if (mapped) {
      const parts = mapped.split('.');
      return parts[parts.length - 1] || mapped;
    }

    return formatFieldId(baseFieldId || fieldId);
  };

  const renderBankAccountValue = (value: unknown) => {
    const stored = value as
      | { selectedId?: string | null; displayLabel?: string | null }
      | null
      | undefined;
    const account = stored?.selectedId
      ? bankAccountsById[stored.selectedId]
      : undefined;

    if (!account) return stored?.displayLabel || '—';

    const rows: Array<{ label: string; value: string }> = [];

    const push = (label: string, val?: string | null) => {
      if (val) rows.push({ label, value: val });
    };

    push('Account holder name', account.accountHolderName);
    push('Bank name', account.bankName);
    push('Account currency', account.currency);
    push('Country', account.country as string);

    const creds = account.credentials as
      | Record<string, string>
      | null
      | undefined;
    if (creds) {
      Object.entries(creds)
        .filter(([, v]) => !!v)
        .forEach(([key, val]) => {
          const label = bankFieldsConfig
            ? getFieldDef(bankFieldsConfig, key)?.label
            : null;
          if (!label) return;
          push(label, key === 'accountType' ? formatAccountType(val) : val);
        });
    }

    if (rows.length === 0) return stored?.displayLabel || '—';

    return (
      <div className="flex flex-col gap-2 w-full">
        {rows.map((r) => (
          <div key={r.label} className="flex flex-col gap-0.5">
            <span className="txt-extra-small-regular text-text-tertiary">
              {r.label}
            </span>
            <span className="txt-small-regular text-text-primary break-words">
              {r.value}
            </span>
          </div>
        ))}
      </div>
    );
  };

  // Transform module entries to GridView data points
  const getDataPointsForEntries = (
    entries: Array<[string, unknown]>
  ): TGridViewDataPoint[] => {
    const dataPoints: TGridViewDataPoint[] = [];
    const hasFileActions = corporationId && (showPreview || showDownload);

    for (const [fieldId, value] of entries) {
      const baseFieldId = fieldId.includes('.')
        ? fieldId.split('.').pop()
        : fieldId;
      const component = componentMap?.[baseFieldId || fieldId] as
        | { component?: string }
        | undefined;

      if (component?.component === 'BankAccount') {
        dataPoints.push({
          heading: getLabel(fieldId),
          value: renderBankAccountValue(value),
        });
        continue;
      }

      const formattedValue = formatValue(value, fieldId, componentMap);
      const isFile = isFileUpload(value) || value instanceof File;
      const isMultiFile = isMultiFileUpload(value);

      if (isMultiFile && hasFileActions) {
        const files = value as Array<Record<string, unknown>>;
        const multiFileValue = (
          <div className="flex flex-col w-full divide-y divide-dashed divide-border-primary">
            {files.map((file, index) => {
              const fileName = (file.fileName as string) || `File ${index + 1}`;
              return (
                <div
                  key={index}
                  className="flex items-center gap-1 py-lg first:pt-0 last:pb-0"
                >
                  <span className="txt-small-regular text-text-primary flex-1">
                    {fileName}
                  </span>
                  {showPreview && (
                    <DataPointButton
                      onClick={() => handlePreview(file)}
                      tooltip="Preview"
                      icon={Icons.Eye}
                    />
                  )}
                  {showDownload && (
                    <DataPointButton
                      onClick={() => handleDownload(file)}
                      tooltip="Download"
                      icon={Icons.Download01}
                    />
                  )}
                </div>
              );
            })}
          </div>
        );

        dataPoints.push({
          heading: getLabel(fieldId),
          value: multiFileValue,
        });
      } else if (isFile && hasFileActions) {
        const actions: TGridViewDataPoint['actions'] = [];
        if (showPreview) {
          actions.push({
            icon: Icons.Eye,
            tooltip: 'Preview',
            onClick: () => handlePreview(value),
          });
        }
        if (showDownload) {
          actions.push({
            icon: Icons.Download01,
            tooltip: 'Download',
            onClick: () => handleDownload(value),
          });
        }

        dataPoints.push({
          heading: getLabel(fieldId),
          value: formattedValue,
          actions,
        });
      } else {
        dataPoints.push({
          heading: getLabel(fieldId),
          value: formattedValue,
        });
      }
    }

    return dataPoints;
  };

  // Determine if module title should be shown
  // Hide title if only 1 module with only 1 field
  const shouldShowModuleTitle = (
    totalModules: number,
    fieldCount: number
  ): boolean => {
    return !(totalModules === 1 && fieldCount === 1);
  };

  // Calculate total modules for conditional title logic
  const getTotalModuleCount = (): number => {
    let count = 0;
    for (const module of modules) {
      if (module.repeatable) {
        const instances = getRepeatableModuleInstances(module);
        count += instances.length;
      } else {
        const entries = getModuleEntriesFlat(module);
        if (entries.length > 0) count += 1;
      }
    }
    return count;
  };

  const totalModules = getTotalModuleCount();

  return (
    <div className="flex flex-col gap-2">
      {description && (
        <p className="txt-small-regular text-text-tertiary mb-4">
          {description}
        </p>
      )}
      <div className="flex flex-col gap-4">
        {Array.isArray(modules) && modules.length > 0 && (
          <div className="space-y-4">
            {modules.map((module: SummaryModule) => {
              if (module.repeatable) {
                const instances = getRepeatableModuleInstances(module);
                if (instances.length === 0) return null;

                return (
                  <div key={module.id} className="space-y-4">
                    {instances.map(
                      ({ instanceIndex, instanceLabel, entries }) => {
                        const dataPoints = getDataPointsForEntries(entries);
                        const showTitle = shouldShowModuleTitle(
                          totalModules,
                          entries.length
                        );

                        return (
                          <div
                            key={`${module.id}-${instanceIndex}`}
                            className="space-y-2"
                          >
                            {showTitle && (
                              <h4 className="text-text-secondary txt-medium-medium">
                                {instanceLabel}
                              </h4>
                            )}
                            <GridView
                              dataPoints={dataPoints}
                              disableRowClick
                            />
                          </div>
                        );
                      }
                    )}
                  </div>
                );
              }

              const moduleEntries = getModuleEntriesFlat(module);
              if (moduleEntries.length === 0) return null;

              // Split entries: regular fields vs custom component fields (NestedForm, KeyPersons)
              const regularEntries = moduleEntries.filter(([fieldId]) => {
                const comp = componentMap?.[fieldId] as
                  | { component?: string }
                  | undefined;
                return (
                  !comp?.component ||
                  !CUSTOM_COMPONENT_TYPES.has(comp.component)
                );
              });
              const nestedFormEntries = moduleEntries.filter(([fieldId]) => {
                const comp = componentMap?.[fieldId] as
                  | { component?: string }
                  | undefined;
                return comp?.component === 'NestedForm';
              });
              const keyPersonsEntries = moduleEntries.filter(([fieldId]) => {
                const comp = componentMap?.[fieldId] as
                  | { component?: string }
                  | undefined;
                return comp?.component === 'KeyPersons';
              });

              const dataPoints = getDataPointsForEntries(regularEntries);
              const totalEntries =
                regularEntries.length +
                nestedFormEntries.length +
                keyPersonsEntries.length;
              const showTitle = shouldShowModuleTitle(
                totalModules,
                totalEntries
              );

              return (
                <div key={module.id} className="space-y-2">
                  {showTitle && (
                    <h4 className="text-text-secondary txt-medium-medium">
                      {module.name || formatFieldId(module.id)}
                    </h4>
                  )}
                  {dataPoints.length > 0 && (
                    <GridView
                      dataPoints={dataPoints}
                      disableRowClick
                    />
                  )}
                  {showNestedFormData &&
                    nestedFormEntries.map(([fieldId, value]) => {
                      const nestedLabel = getLabel(fieldId);

                      const entryArray: Record<string, unknown>[] =
                        Array.isArray(value)
                          ? (value as Record<string, unknown>[])
                          : value != null &&
                              typeof value === 'object' &&
                              !Array.isArray(value)
                            ? [value as Record<string, unknown>]
                            : [];

                      if (entryArray.length === 0) return null;

                      // Get keyId from the parent componentMap to fetch labels
                      const nestedComp = componentMap?.[fieldId] as
                        | { component?: string; props?: { keyId?: string } }
                        | undefined;
                      const keyId = nestedComp?.props?.keyId ?? '';

                      return (
                        <NestedFormSummarySection
                          key={fieldId}
                          fieldId={fieldId}
                          entries={entryArray}
                          keyId={keyId}
                          sectionLabel={nestedLabel}
                          onDownload={
                            corporationId ? handleDownload : undefined
                          }
                          onPreview={
                            corporationId && showPreview
                              ? handlePreview
                              : undefined
                          }
                        />
                      );
                    })}
                  {showNestedFormData &&
                    keyPersonsEntries.map(([fieldId, value]) => {
                      if (!isKeyPersonsData(value)) return null;
                      const { selectedIds, entries: personEntries } = value;
                      if (selectedIds.length === 0) return null;

                      const comp = componentMap?.[fieldId] as
                        | {
                            component?: string;
                            label?: string;
                            props?: { keyId?: string };
                          }
                        | undefined;
                      const keyId = comp?.props?.keyId ?? '';
                      const sectionLabel = comp?.label || getLabel(fieldId);

                      const entryArray = selectedIds
                        .map((pid) => personEntries[pid])
                        .filter((e): e is Record<string, unknown> => e != null);

                      if (entryArray.length === 0) return null;

                      return (
                        <NestedFormSummarySection
                          key={fieldId}
                          fieldId={fieldId}
                          entries={entryArray}
                          keyId={keyId}
                          sectionLabel={sectionLabel}
                          onDownload={
                            corporationId ? handleDownload : undefined
                          }
                          onPreview={
                            corporationId && showPreview
                              ? handlePreview
                              : undefined
                          }
                        />
                      );
                    })}
                </div>
              );
            })}
          </div>
        )}

        {allFormEntries.length === 0 && (
          <p className="txt-small-regular text-text-tertiary text-center py-4">
            No data has been entered yet. Fill out the form to see a summary
            here.
          </p>
        )}
      </div>

      <Dialog open={isPreviewOpen} onOpenChange={handleClosePreview}>
        <DialogContent className="flex flex-col gap-4 max-w-[800px]">
          <DialogHeader className="flex flex-col items-start gap-4">
            <div className="flex flex-col gap-1">
              <DialogTitle>{previewFileName || 'Preview file'}</DialogTitle>
            </div>
          </DialogHeader>

          <div className="h-[500px] overflow-y-auto p-6 bg-bg-secondary">
            {isLoadingPreview ? (
              <div className="flex justify-center items-center h-full">
                <LoadingIndicator />
              </div>
            ) : (
              previewFileUrl && <DocViewerComponent uri={previewFileUrl} />
            )}
          </div>

          <DialogFooter>
            <div className="flex justify-end w-full gap-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={handleClosePreview}
              >
                Close
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
